import { getConfig, getRequestHeaders } from '../config';
import { MATTING_MTCC_FUNC_CODE } from '../constants';
import { base64ToFile } from '../utils/image';
import { polling } from '../utils/polling';
import { mergeHumanParsingMaskImages } from './human-parsing';
import { getMattingPreset } from './preset';
import { MattingType, SegmentationLabel, SodResult } from './types';

/**
 * 获取图片基础蒙版信息。
 * @param isClothseg 是否服装抠图
 * @param isUseNewAPI 是否使用新分割算法
 * @param params.mattingType 抠图类型：不填为自动识别。0-人像 1-物体 2-图标
 * @param params.imageURL 图片URL。
 * @param params.headers['x-mtcc-client'] 成本埋点:https://cf.meitu.com/confluence/pages/viewpage.action?pageId=416230853
 */
interface SodParams {
    mattingType?: MattingType;
    imageURL: string;
    client_id?: string;
    preview_mode?: number;
    gid?: string;
    // from、project_id、project_info三个字段，服务端创建历史记录需要
    from?: string;
    project_id?: number;
    project_info?: string;
    innerSeg?: boolean;
    userboxes?: Array<Array<Array<number>>>;
    client_language?: string;
    country_code?: string;
    highResolution?: boolean;
    /* 保持原尺寸，设置为false算法会压缩为2496 */
    keepResolution?: boolean;
    /* 是否使用前景估计，算法默认false，如果边缘存在白边问题可以设置为true */
    useFeRgba?: boolean;
    /* 控制是否使用高分辨率图像进行推理，默认为False。如果图像分辨率高于2K，建议设置为True */
    useHighRes?: boolean;
    // 衣服分割请求数据, mattingType指定衣服分割，必填
    humanParsingConfig?: Omit<HumanParsingConfig, 'originalImageURL'>;
    fromDemo?: boolean;
    education_role?: string;
}

interface SodConfig {
    isClothseg?: boolean;
    isUseNewAPI?: boolean;
    // 用新的sod_2c 接口
    isUseNew2CAPI?: boolean;
    transfer?: string;
    isRemoveAK?: boolean;
    params: SodParams;
    nbox?: boolean;
    transferID?: string;
}

interface SodRes extends SodResult {
    project_id?: number;
    position?: number[];
}

interface ApiResponse {
    data: {
        media_info_list?: Array<{
            media_data?: string;
        }>;
        seal_bbox?: [number, number, number, number];
        parameter?: {
            Kind: MattingType;
            exist_salient: boolean;
            use_fe: boolean;
            return_fe: boolean;
            top_x?: string;
            top_y?: string;
            bottom_x?: string;
            bottom_y?: string;
            seal_bbox?: [number, number, number, number];
        };
        evaluation_valid: boolean;
        project_id?: number;
        msg_id?: string;
    };
    error_code?: number;
    error?: string;
    code?: number;
    message?: string;
}

export async function getImageBaseMask({
    isClothseg = false,
    isUseNewAPI = false,
    isUseNew2CAPI = false,
    nbox = false,
    transfer,
    transferID,
    isRemoveAK,
    params,
}: SodConfig): Promise<SodRes> {
    const { request, requestOptions, uploadFileToObs } = getConfig();

    // ATTENTION: 新版 sod_2c 接口仅在 webapi 上有
    const sodAPI = isUseNew2CAPI ? '/v1/matting/sod_c' : isUseNewAPI ? '/v1/matting/sod_2c' : '/v1/matting/sod';
    const apiUrl = isClothseg ? '/v1/matting/mttryon_clothseg' : sodAPI;
    const url = `${requestOptions?.baseURL || ''}${apiUrl}`;

    const resultData: SodRes = {
        code: 0,
        message: 'OK',

        mattingType: 0,
        maskURL: '',
        useBlurFilterRun: false,
        foregroundEstimateImageURL: '',
        isFirstUsedForCurrentImage: false,
        position: [],
    };

    const headers = await getRequestHeaders(MATTING_MTCC_FUNC_CODE, isRemoveAK);

    if (params.mattingType === MattingType.Clothing && params.humanParsingConfig) {
        // 走衣服分割
        resultData.mattingType = MattingType.Clothing;
        try {
            const res = await getHumanParsingRes({ ...params.humanParsingConfig, originalImageURL: params.imageURL });
            if (res.data?.media_info_list.length) {
                const urls = res.data?.media_info_list.map((item) => item.media_data);
                const base64 = await mergeHumanParsingMaskImages(urls);
                if (uploadFileToObs && params.humanParsingConfig.uploadMask) {
                    const file = base64ToFile(base64, '');
                    const res = await uploadFileToObs(file);
                    resultData.maskURL = res.url;
                } else {
                    resultData.maskURL = base64;
                }
            }
            return resultData;
        } catch (e: any) {
            resultData.code = -1;
            resultData.message = e.message || 'network error';
            return resultData;
        }
    }

    return request<ApiResponse>(url, {
        method: 'POST',
        withCredentials: true,
        headers,
        params: {
            client_id: params.client_id,
            preview_mode: params.preview_mode,
            gid: params.gid,
            client_language: params.client_language,
            country_code: params.country_code,
            from: params.from,
            education_role: params.education_role,
            project_id: params.project_id,
            project_info: params.project_info,
            save_to_own_bucket: transfer,
            from_demo: params.fromDemo ? 1 : 0,
            transfer_id: transferID,
        },
        data: {
            parameter: {
                rsp_media_type: 'url',
                nMask: true,
                nbox,
                model_type: params.mattingType,
                inner_seg: params.innerSeg,
                userboxes: params.userboxes ? JSON.stringify(params.userboxes) : undefined,
                high_resolution: params.highResolution,
                keep_resolution: params.keepResolution,
                use_fe_rgba: params.useFeRgba,
                use_high_res: params.useHighRes,
            },
            media_info_list: [
                {
                    media_data: params.imageURL,
                    media_profiles: {
                        media_data_type: 'url',
                    },
                },
            ],
        },
    })
        .then((res) => {
            if (res.data.code || res.data.error_code) {
                resultData.code = res.data.code || res.data.error_code || 0;
                resultData.message = res.data.message || res.data.error || '';
                return resultData;
            }

            if (res.data.data) {
                if (res.data.data?.parameter?.exist_salient === false) {
                    resultData.code = res.data.code || res.data.error_code || 0;
                    resultData.message = res.data.message || res.data.error || '未识别到人像/物品/图标';
                    // 未识别 定义为关闭
                    resultData.mattingType = MattingType.Close;
                    return resultData;
                }

                if (typeof res.data.data?.parameter?.Kind === 'number') {
                    resultData.mattingType = res.data.data.parameter.Kind;
                }

                if (typeof res.data.data?.parameter?.use_fe === 'boolean') {
                    resultData.useBlurFilterRun = res.data.data.parameter.use_fe;
                }

                const sealBBox = res.data.data?.seal_bbox || res.data.data?.parameter?.seal_bbox;
                if (Array.isArray(sealBBox) && sealBBox.length === 4) {
                    resultData.seal_bbox = sealBBox as [number, number, number, number];
                }

                if (nbox) {
                    resultData.position = [
                        Number(res.data.data?.parameter?.top_x || 0),
                        Number(res.data.data?.parameter?.top_y || 0),
                        Number(res.data.data?.parameter?.bottom_x || 0),
                        Number(res.data.data?.parameter?.bottom_y || 0),
                    ];
                }

                // 使用云端算法前景估计图
                if (
                    typeof res.data.data?.parameter?.return_fe === 'boolean' &&
                    res.data?.data?.media_info_list &&
                    res.data?.data?.media_info_list[1]?.media_data
                ) {
                    resultData.foregroundEstimateImageURL = res.data.data.media_info_list[1].media_data;
                }

                if (res.data?.data?.media_info_list && res.data?.data?.media_info_list[0]?.media_data) {
                    resultData.maskURL =
                        getMattingPreset(params.imageURL, params.mattingType) ||
                        res.data.data.media_info_list[0].media_data;
                    resultData.isFirstUsedForCurrentImage = res.data.data.evaluation_valid;
                }

                // 项目id
                if (res.data?.data?.project_id) {
                    resultData.project_id = res.data?.data?.project_id;
                }

                if (res.data.data?.msg_id) {
                    resultData.msg_id = res.data.data?.msg_id;
                }

                return resultData;
            }
            resultData.code = res.data.code || res.data.error_code || 0;
            resultData.message = res.data.message || res.data.error || '';
            return resultData;
        })
        .catch((err) => {
            resultData.code = err.response?.data?.code || err.response?.data?.error_code || -1;
            resultData.message = err.response?.data?.message || err.response?.data?.error || 'network error';
            return resultData;
        });
}

/**
 * 获取附加选区信息的蒙版。
 * @param url 请求url。
 * @param params.imageOriginalURL imageURL
 * @param params.imageBaseMaskURL 基础maskURL
 * @param params.areaList 交互点位置列表 [[添加/删除,交互点纵坐标百分比,交互点横坐标百分比]]
 * @param params.headers['x-mtcc-client'] 成本埋点:https://cf.meitu.com/confluence/pages/viewpage.action?pageId=416230853
 */
export async function getImageMaskWithAreas({
    params,
}: {
    params: {
        imageOriginalURL: string;
        imageBaseMaskURL: string;
        areaList: Array<[0 | 1, number, number]>;
        areaType?: 0 | 1;
        client_id?: string;
        preview_mode?: number;
        client_language?: string;
        country_code?: string;
        gid?: string;
        highResolution?: boolean;
        transfer?: string;
    };
}) {
    const { request, requestOptions } = getConfig();

    const url = `${requestOptions?.baseURL || ''}/v1/matting/interseg`;

    const headers = await getRequestHeaders(MATTING_MTCC_FUNC_CODE);

    const media_info_list = [
        {
            media_data: params.imageOriginalURL,
            media_extra: {},
            media_profiles: {
                media_data_type: 'url',
            },
        },
    ];
    if (params.imageBaseMaskURL) {
        media_info_list.push({
            media_data: params.imageBaseMaskURL,
            media_extra: {},
            media_profiles: {
                media_data_type: 'url',
            },
        });
    }
    return request<{
        data: {
            parameter: {};
            media_info_list: Array<{
                media_data: string;
            }>;
        };
    }>(url, {
        method: 'POST',
        withCredentials: true,
        headers,
        params: {
            client_id: params.client_id,
            preview_mode: params.preview_mode,
            gid: params.gid,
            client_language: params.client_language,
            country_code: params.country_code,
            save_to_own_bucket: params.transfer,
        },
        data: {
            parameter: {
                rsp_media_type: 'url',
                click_list: params.areaList,
                mask_type: 2,
                click_type: params.areaType || 0,
                high_resolution: params.highResolution,
            },
            media_info_list,
        },
    }).then((res) => {
        if (res.data.data) {
            const resultData = {
                imageOriginalURL: params.imageOriginalURL,
                imageBaseMaskURL: params.imageBaseMaskURL,
                imageWithAreasMaskURL: '',
            };
            if (res.data.data.media_info_list[0]) {
                resultData.imageWithAreasMaskURL = res.data.data.media_info_list[0].media_data;
            }
            return resultData;
        }
        return null;
    });
}

/**
 * 获取交互分割蒙版。
 * @param url 请求url。
 * @param data.originalImgURL imageURL
 * @param data.maskImgURL 基础maskURL
 * @param data.clickList 交互点位置列表 [[交互点纵坐标百分比,交互点横坐标百分比]]
 * @doc https://cf.meitu.com/confluence/pages/viewpage.action?pageId=506888391
 */
type ScribbleInteractReqData = {
    originalImgURL?: string;
    maskImgURL?: string;
    clickList: Array<[number, number]>;
    segType?: 0 | 1;
    brushWidth: number;
    recordUuid: string;
    operationUuid: string;
    preOperationUuid?: string;
    resType?: 'jpg' | 'url';
    sceneType: 'all' | 'logo';
    sodSize?: '1' | '0';
};

export async function getScribbleInteractSeg({
    params,
    data,
}: {
    params: {
        client_id?: string;
        preview_mode?: number;
        client_language?: string;
        country_code?: string;
        gid?: string;
        highResolution?: boolean;
        transfer?: string;
    };
    data: ScribbleInteractReqData;
}) {
    const { request, requestOptions } = getConfig();
    const url = `${requestOptions?.baseURL || ''}/v1/matting/scribbleinteractseg`;
    const headers = await getRequestHeaders(MATTING_MTCC_FUNC_CODE);

    const media_info_list = [];
    if (data.originalImgURL) {
        media_info_list.push({
            media_data: data.originalImgURL,
            media_extra: {},
            media_profiles: {
                media_data_type: 'url',
            },
        });
    }
    if (data.maskImgURL) {
        media_info_list.push({
            media_data: data.maskImgURL,
            media_extra: {},
            media_profiles: {
                media_data_type: 'url',
            },
        });
    }
    return request<{
        data: {
            parameter: {};
            duration?: {
                alg_process_time: number;
                created_timestamp: number;
                pull_timestamp: number;
                upload_time: number;
                waiting_time: number;
            };
            media_info_list: Array<{
                media_data: string;
                media_profiles: {
                    media_data_type: 'jpg' | 'url';
                };
            }>;
        };
    }>(url, {
        method: 'POST',
        withCredentials: true,
        headers,
        params: {
            client_id: params.client_id,
            preview_mode: params.preview_mode,
            gid: params.gid,
            client_language: params.client_language,
            country_code: params.country_code,
            save_to_own_bucket: params.transfer,
        },
        data: {
            parameter: {
                rsp_media_type: data.resType || 'url',
                click_list: data.clickList,
                segType: String(data.segType),
                brush_width: String(data.brushWidth),
                recordUuid: data.recordUuid,
                operationUuid: data.operationUuid,
                preOperationUuid: data.preOperationUuid,
                img: '1',
                pre: data.originalImgURL && !data.maskImgURL ? '-1' : '2',
                scene_type: data.sceneType,
                sod_size: data.sodSize,
                // 0 单通道, 1 四通道
                nMask: '1',
            },
            media_info_list,
        },
    }).then((res) => {
        if (res.data.data) {
            const resultData = {
                maskURL: '',
            };
            if (res.data.data.media_info_list[0]) {
                if (res.data.data.media_info_list[0].media_profiles?.media_data_type === 'jpg') {
                    resultData.maskURL = 'data:image/jpeg;base64,' + res.data.data.media_info_list[0].media_data;
                } else {
                    resultData.maskURL = res.data.data.media_info_list[0].media_data;
                }
            }
            return resultData;
        }
        return null;
    });
}

/** 人体检测 **/
interface HumanParsingResponse {
    data: {
        msg_id: string;
    };
    error_code?: number;
    error?: string;
    code?: number;
    message?: string;
}
export interface HumanParsingRequest {
    // 原图
    originalImageURL: string;
    // 需要获取的标签类型
    segmentationLabels: SegmentationLabel[];
    // 转存桶
    budget?: string;
    // 自动压缩阈值尺寸（2048）
    autoResize?: boolean;
}

/**
 * @docs 接口文档 https://api-mock.meitu-int.com/project/2110/interface/api/180511
 */
export const humanParsing = async (params: HumanParsingRequest) => {
    const { originalImageURL, budget, autoResize = true } = params;
    const { request, requestOptions } = getConfig();
    const url = `${requestOptions?.baseURL || ''}/v1/mtlab/humanparsing_async`;
    const headers = await getRequestHeaders(MATTING_MTCC_FUNC_CODE);
    const segResponse = await request<HumanParsingResponse>(url, {
        method: 'POST',
        headers,
        params: {
            save_to_own_bucket: budget,
        },
        data: {
            original_url: originalImageURL,
            extra: {},
            media_info_list: [
                {
                    media_data: originalImageURL,
                    media_extra: {},
                    media_profiles: {
                        media_data_type: 'url',
                    },
                },
            ],
            parameter: {
                rsp_media_type: 'url',
                postprocess: '1',
                conflict: '1',
                mergeMask: '1',
                // 识别类，34、19
                classes_type: '34',
                need_reshape: autoResize,
                // ignore_label: [0, 1, 2, 3, 4, 5, 6],
                // ignore_label: [
                //     0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
                //     26, 27, 28, 29, 30, 31, 32, 33,
                // ],
            },
        },
    });

    const msgData = segResponse.data;
    if (msgData.code !== 0 || !msgData.data) {
        throw new Error(`humanparsing_async failed.code: ${msgData.code}`);
    }

    return msgData.data.msg_id;
};

export interface QueryResponse {
    data?: {
        media_info_list: Array<{
            media_data: string;
            media_extra: {
                mask_label: SegmentationLabel;
            };
            media_profiles: {
                media_data_type: 'jpg' | 'url';
            };
        }>;
    };
    error_code?: number;
    error?: string;
    code?: number;
    message?: string;
}

/**
 * @docs 接口文档 https://api-mock.meitu-int.com/project/2110/interface/api/122407
 */
export const queryMulti = async (msg_id: string) => {
    const { request, requestOptions } = getConfig();
    const url = `${requestOptions?.baseURL || ''}/v1/mtlab/query`;
    const headers = await getRequestHeaders(MATTING_MTCC_FUNC_CODE);
    const multiData = await request<QueryResponse>(url, {
        method: 'GET',
        headers,
        params: {
            msg_id,
        },
    });

    return multiData.data;
};

// 轮询任务
async function loopTaskStatus(taskId: string, timeout?: number): Promise<QueryResponse> {
    const result = await polling(
        // 异步任务
        async () => {
            const response = await queryMulti(taskId);
            return response;
        },
        // err_code 0 成功 29901 进行中
        (result: QueryResponse) => {
            return result.code !== 29901;
        },
        // 配置选项
        {
            interval: 1000,
            timeout: timeout || 60 * 1000,
            onPolling: () => {
                // 轮询 `第 ${attempt} 次检查任务状态`
            },
        },
    );

    return result;
}

type HumanParsingConfig = HumanParsingRequest & {
    // 轮询超时时间
    timeout?: number;
    // 重新上传衣服分割mask
    uploadMask?: boolean;
};

// 衣服分割
export async function getHumanParsingRes(params: HumanParsingConfig): Promise<QueryResponse> {
    const task_id = await humanParsing(params);
    const { segmentationLabels } = params;

    const res = await loopTaskStatus(task_id, params.timeout);

    // 按需过滤一下识别标签
    if (res.data?.media_info_list) {
        res.data.media_info_list = res.data.media_info_list.filter((item) => {
            return segmentationLabels.includes(item.media_extra.mask_label);
        });
    }

    return res;
}
